import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import {
  Alert,
  Col,
  ControlLabel,
  ListGroup,
  ListGroupItem,
  Modal,
  Row,
} from 'react-bootstrap';
import Button from '../../../../common/components/Button/Button';
import { clearFiles, removeFile } from '../../../../common/components/Upload/UploadRedux';
import { closeModal, openModal } from '../../../../common/components/Modal/ModalRedux';
import UploadModalComponent, {
  UploadModal,
} from '../../../../common/components/Upload/UploadModal';
import { clearSchemes, confirmImport, importConfirmed } from './redux/schemeImportAction';
import './SchemesImportModal.scss';
import { ButtonBar } from '../../../../common/components';

const MESSAGES = {
  upload: 'Please select a scheme to upload.',
  imported: 'Scheme(s) imported successfully.',
  error: 'There was an error importing the schemes(s)',
};

const UploadButton = ({
  hasFiles,
  isImported,
  isPopulated,
  isLoading,
  onDocumentUpload,
  onImport,
}) => {
  if (isImported) {
    return null;
  }

  if (hasFiles) {
    return (
      <Button
        type="button"
        bsStyle="primary"
        className="pull-right pad-right"
        handleClick={onImport}
      >
        {isLoading ? <i className="fa fa-cog fa-spin" /> : <i className="fa fa-upload" />}{' '}
        {isPopulated ? 'Confirm Import' : 'Upload File'}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      bsStyle="primary"
      className="pull-right pad-right"
      handleClick={onDocumentUpload}
    >
      <i className="fa fa-upload" /> Select scheme file
    </Button>
  );
};
const UploadItem = ({ index, onRemoveFile, fileName }) => {
  const handleRemoveItem = () => {
    onRemoveFile(index);
  };
  return (
    <li key={index}>
      <div className="upload-progress" />
      <a onClick={handleRemoveItem}>
        <i className="fa fa-times" />
      </a>
      <div className="content">
        <p className="pull-right">&nbsp;</p> {fileName}
      </div>
    </li>
  );
};
const Uploads = ({ files, isPopulated, onRemoveFile }) => {
  if (!files.length || isPopulated) {
    return null;
  }

  return (
    <Row>
      <Col xs={12}>
        <ControlLabel>Attachments:</ControlLabel>
        <ul className="upload-list">
          {files.map((file, i) => (
            <UploadItem
              fileName={file.attributes.name}
              index={i}
              onRemoveFile={onRemoveFile}
            />
          ))}
        </ul>
      </Col>
    </Row>
  );
};

const SchemeGroupList = ({ schemeGroups }) => {
  if (!schemeGroups?.length) {
    return null;
  }
  return (
    <>
      <div className={'schemes-import-modal--title'}> To Be Imported: Scheme Groups</div>
      <ListGroup>
        {schemeGroups.map((scheme, key) => {
          return (
            <ListGroupItem key={key}>
              {scheme.name} v{scheme.version}
            </ListGroupItem>
          );
        })}
      </ListGroup>
    </>
  );
};

const SchemeList = ({ schemeGroups, schemes }) => {
  if (!schemes?.length) {
    return null;
  }

  const title = schemeGroups?.length ? 'Schemes (Derived from Scheme Group)' : 'Schemes';

  return (
    <>
      <SchemeGroupList schemeGroups={schemeGroups} />
      <div className={'schemes-import-modal--title'}> To Be Imported: {title} </div>
      <ListGroup>
        {schemes.map((scheme, key) => {
          return (
            <ListGroupItem key={key}>
              {scheme.name} v{scheme.version}
            </ListGroupItem>
          );
        })}
      </ListGroup>
    </>
  );
};
const SchemesImportModal = ({
  removeFile,
  clearFiles,
  clearSchemes,
  openUploadModal,
  confirmImport,
}) => {
  const [show, setShow] = useState(false);
  const {
    isPopulated,
    isImported,
    isError,
    isLoading,
    schemes,
    schemeGroups,
  } = useSelector((state) => state.schemeImport);
  const { files } = useSelector((state) => state.upload);

  const openModal = () => {
    setShow(true);
  };

  const closeModal = () => {
    clearFiles();
    clearSchemes();
    setShow(false);
  };

  const handleDocumentUpload = () => {
    openUploadModal(UploadModal.MODAL_IDENTIFIER);
  };

  const handleImport = () => {
    const headFile = files?.[0];
    if (isPopulated) {
      return Promise.resolve(confirmImport(headFile.id, isPopulated)).then(() => {
        setShow(false);
      });
    }
    return confirmImport(headFile.id, isPopulated);
  };

  const hasFiles = !!files?.length;
  return (
    <>
      <Button bsStyle="primary" className="pad-right" handleClick={openModal}>
        Import Schemes
      </Button>
      <div className="schemes-import-modal">
        <Modal show={show} onHide={closeModal}>
          <Modal.Header closeButton>
            <Modal.Title>Import Schemes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="import-wrap">
              <Uploads
                files={files}
                onRemoveFile={removeFile}
                isPopulated={isPopulated}
              />

              <SchemeList schemes={schemes} schemeGroups={schemeGroups} />
              {!isPopulated && !isImported && MESSAGES.upload}
              {isImported && <Alert bsStyle={'success'}>{MESSAGES.imported}</Alert>}
              {isError && <Alert bsStyle={'danger'}>{MESSAGES.error}</Alert>}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <ButtonBar>
              <UploadButton
                hasFiles={hasFiles}
                isImported={isImported}
                isPopulated={isPopulated}
                isLoading={isLoading}
                onDocumentUpload={handleDocumentUpload}
                onImport={handleImport}
              />
              <Button
                type="button"
                bsStyle="default"
                className="pull-right pad-right"
                handleClick={closeModal}
              >
                Cancel
              </Button>
            </ButtonBar>
          </Modal.Footer>
        </Modal>
      </div>
      <UploadModalComponent />
    </>
  );
};

export default connect(
  null,
  {
    openUploadModal: openModal,
    closeModal: closeModal,
    clearFiles: clearFiles,
    removeFile: removeFile,
    confirmImport: confirmImport,
    clearSchemes: clearSchemes,
    importConfirmed: importConfirmed,
  },
)(SchemesImportModal);
